import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard, Building2, Package, Star, Settings, Inbox, Users, Activity,
  Receipt, Truck, UserCircle, FileText, Bell, LogOut, ChevronLeft, Shield,
  ChevronDown, Menu, Calendar, BarChart3, ClipboardCheck, HeartPulse, ShieldAlert,
  Lock, Wrench, Clock, Pill, TrendingUp, ShieldCheck, Target, Hospital, Send, Map as MapIcon,
  Radio, CheckCircle2, BadgeCheck, Award, DollarSign, CreditCard, Ambulance, AlertCircle
} from 'lucide-react';
import UpgradeModal from './UpgradeModal';
import BrandLogo from './BrandLogo';
import { ImpersonationPicker } from './Impersonation';
import { isSectionLocked as isSectionLockedForUser } from '../utils/modules';

// Sidebar agrupado: cada sección lleva 'group' para mostrarse bajo un header
// común. El render dibuja un encabezado discreto la primera vez que aparece
// un grupo. Esto evita listas planas largas (Admin tenía 21 entradas).
const PORTAL_CONFIG = {
  superadmin: {
    label: 'Super Admin',
    icon: Shield,
    color: 'var(--purple)',
    sections: [
      // Vista general
      { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, group: 'Vista general' },
      { id: 'tenantMetrics', label: 'Métricas Tenant', icon: BarChart3, group: 'Vista general' },
      // Clientes SaaS
      { id: 'tenants', label: 'Clientes SaaS', icon: Building2, group: 'Clientes SaaS' },
      { id: 'users', label: 'Usuarios', icon: Users, group: 'Clientes SaaS' },
      { id: 'subscriptions', label: 'Suscripciones', icon: CreditCard, group: 'Clientes SaaS' },
      { id: 'tickets', label: 'Soporte', icon: Inbox, group: 'Clientes SaaS' },
      // Catálogo plataforma
      { id: 'plans', label: 'Planes', icon: Star, group: 'Catálogo plataforma' },
      { id: 'pricing', label: 'Pricing & Promos', icon: DollarSign, group: 'Catálogo plataforma' },
      { id: 'modules', label: 'Módulos', icon: Package, group: 'Catálogo plataforma' },
      { id: 'categories', label: 'Categorías profesionales', icon: Users, group: 'Catálogo plataforma' },
      { id: 'vehicleTypes', label: 'Tipos vehículo', icon: Truck, group: 'Catálogo plataforma' },
      // Sistema
      { id: 'health', label: 'Salud sistema', icon: HeartPulse, group: 'Sistema' },
      { id: 'audit', label: 'Audit log', icon: ShieldAlert, group: 'Sistema' },
      { id: 'settings', label: 'Ajustes', icon: Settings, group: 'Sistema' },
    ],
  },
  admin: {
    label: 'Panel Admin',
    icon: Building2,
    color: 'var(--accent)',
    sections: [
      // Vista general — KPIs y reporting cross-módulo (al tope, acceso rápido)
      { id: 'dashboard', label: 'Panel', icon: LayoutDashboard, group: 'Vista general' },
      { id: 'executive', label: 'Vista ejecutiva', icon: TrendingUp, group: 'Vista general' },
      { id: 'reporting', label: 'Informes', icon: BarChart3, group: 'Vista general' },
      // Operaciones tiempo real — corazón del despacho diario.
      // dispatch primero (más usado), después DRP y traslados, luego planificación.
      { id: 'dispatch', label: 'Centralita', icon: Radio, group: 'Operaciones tiempo real' },
      { id: 'dispatch_map', label: 'Mapa centralita', icon: MapIcon, group: 'Operaciones tiempo real' },
      { id: 'requests', label: 'DRP', icon: AlertCircle, group: 'Operaciones tiempo real' },
      { id: 'transport_requests', label: 'Servicios sanitarios', icon: Ambulance, group: 'Operaciones tiempo real' },
      { id: 'calendar', label: 'Calendario', icon: Calendar, group: 'Operaciones tiempo real' },
      { id: 'services', label: 'Servicios en curso', icon: Clock, group: 'Operaciones tiempo real' },
      // Red asistencial — workflow clínico/calidad, distinto del operativo puro.
      { id: 'patients', label: 'Partes clínicos', icon: FileText, group: 'Red asistencial' },
      { id: 'hospitals', label: 'Hospitales', icon: Hospital, group: 'Red asistencial' },
      { id: 'quality', label: 'Calidad / ISO', icon: CheckCircle2, group: 'Red asistencial' },
      // Comercial & finanzas — iconos diferenciados (rates ≠ billing).
      { id: 'clients', label: 'Clientes', icon: Users, group: 'Comercial' },
      { id: 'leads', label: 'Leads / CRM', icon: Target, group: 'Comercial' },
      { id: 'rates', label: 'Tarifas', icon: DollarSign, group: 'Comercial' },
      { id: 'billing', label: 'Facturación', icon: CreditCard, group: 'Comercial' },
      // Personal sanitario — iconos únicos (no repite Activity).
      { id: 'professionals', label: 'Profesionales', icon: BadgeCheck, group: 'Personal' },
      { id: 'certifications', label: 'Certificaciones', icon: Award, group: 'Personal' },
      { id: 'shifts', label: 'Turnos y fichaje', icon: Clock, group: 'Personal' },
      // Flota & material — todo lo físico.
      { id: 'resources', label: 'Recursos', icon: Truck, group: 'Flota y material' },
      { id: 'checklists', label: 'Checklists', icon: ClipboardCheck, group: 'Flota y material' },
      { id: 'maintenance', label: 'Mantenimiento', icon: Wrench, group: 'Flota y material' },
      { id: 'stock', label: 'Inventario', icon: Pill, group: 'Flota y material' },
      { id: 'insurance', label: 'Pólizas seguros', icon: Shield, group: 'Flota y material' },
      // Configuración del tenant.
      { id: 'users', label: 'Usuarios', icon: UserCircle, group: 'Configuración' },
      { id: 'custom_roles', label: 'Roles personalizados', icon: ShieldCheck, group: 'Configuración' },
      { id: 'settings', label: 'Ajustes', icon: Settings, group: 'Configuración' },
    ],
  },
  client: {
    label: 'Portal Cliente',
    icon: Users,
    color: 'var(--blue)',
    sections: [
      { id: 'dashboard', label: 'Resumen', icon: LayoutDashboard },
      { id: 'requests', label: 'Mis Solicitudes', icon: Inbox },
      { id: 'quotes', label: 'Presupuestos', icon: FileText },
      { id: 'invoices', label: 'Facturas', icon: Receipt },
    ],
  },
  professional: {
    label: 'App Profesional',
    icon: Activity,
    color: 'var(--gold)',
    sections: [
      { id: 'dashboard', label: 'Mi Panel', icon: LayoutDashboard },
      { id: 'services', label: 'Servicios', icon: Truck },
      { id: 'availability', label: 'Disponibilidad', icon: Calendar },
      { id: 'profile', label: 'Mi Perfil', icon: UserCircle },
      { id: 'billing', label: 'Liquidaciones', icon: Receipt },
    ],
  },
  ambulance: {
    label: 'Recurso',
    icon: Truck,
    color: '#FF6B4A',
    sections: [
      { id: 'dashboard', label: 'Panel', icon: LayoutDashboard },
      { id: 'dispatch', label: 'Centralita', icon: Send },
      { id: 'patients', label: 'Pacientes', icon: Users },
      { id: 'history', label: 'Historial', icon: FileText },
    ],
  },
  hospital: {
    label: 'Portal Hospital',
    icon: Hospital,
    color: '#8B5CF6',
    sections: [
      { id: 'dashboard', label: 'Resumen', icon: LayoutDashboard },
      { id: 'service_requests', label: 'Solicitar servicio', icon: Send },
      { id: 'patients', label: 'Pacientes derivados', icon: HeartPulse },
    ],
  },
};

export default function Sidebar({ currentSection, onSectionChange, viewAs, onViewAsChange }) {
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [showSwitcher, setShowSwitcher] = useState(false);
  const [pickerRole, setPickerRole] = useState(undefined); // undefined = cerrado
  const [upgradeFeature, setUpgradeFeature] = useState(null);

  const effectiveRole = (user?.role === 'superadmin' && viewAs) ? viewAs : (user?.role || 'admin');
  const portal = PORTAL_CONFIG[effectiveRole];
  if (!portal) return null;

  const PortalIcon = portal.icon;
  const isSuperAdmin = user?.role === 'superadmin';
  const tenantLogo = user?.tenant_logo;
  const tenantSettings = user?.tenant_settings || {};

  // Estado de grupos colapsados — persistido por rol en localStorage.
  // Si el usuario navega a una sección de un grupo colapsado, se auto-expande.
  const collapseKey = `mc_sidebar_groups_${effectiveRole}`;
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(collapseKey) || '[]')); }
    catch (_) { return new Set(); }
  });
  const toggleGroup = (group) => {
    const next = new Set(collapsedGroups);
    if (next.has(group)) next.delete(group); else next.add(group);
    setCollapsedGroups(next);
    try { localStorage.setItem(collapseKey, JSON.stringify([...next])); } catch (_) {}
  };
  // Auto-expandir el grupo de la sección actual si está colapsado
  useEffect(() => {
    const active = portal.sections.find(s => s.id === currentSection);
    if (active?.group && collapsedGroups.has(active.group)) {
      const next = new Set(collapsedGroups);
      next.delete(active.group);
      setCollapsedGroups(next);
      try { localStorage.setItem(collapseKey, JSON.stringify([...next])); } catch (_) {}
    }
  }, [currentSection]);

  // Apply tenant colors to CSS variables
  useEffect(() => {
    if (tenantSettings.primary_color) document.documentElement.style.setProperty('--accent', tenantSettings.primary_color);
    if (tenantSettings.secondary_color) document.documentElement.style.setProperty('--blue', tenantSettings.secondary_color);
    return () => { document.documentElement.style.removeProperty('--accent'); document.documentElement.style.removeProperty('--blue'); };
  }, [tenantSettings.primary_color, tenantSettings.secondary_color]);

  // Module-gating: show ALL sections but mark gated ones as `locked` so UI shows a padlock.
  // SuperAdmin always sees unlocked. Si un tenant no tiene enabled_modules fijados, no hay restricciones.
  //

  // Bloqueo por módulos del plan: utils/modules.js (mismo mapa que aplica el backend)
  const isSectionLocked = (sectionId) => {
    if (isSuperAdmin || effectiveRole === 'superadmin') return false;
    return isSectionLockedForUser(user, sectionId);
  };

  // Soft-RBAC por custom role del usuario admin: si user.allowed_sections
  // está definido (array), filtramos a SOLO esas secciones. Si es null el
  // admin ve todo (comportamiento por defecto). Solo aplica cuando el rol
  // base es 'admin' — los otros portales (client/professional/hospital/...)
  // tienen su propio scope de secciones definido en PORTAL_CONFIG.
  const allowedSections = user?.allowed_sections;  // array o null
  const customRoleActive = effectiveRole === 'admin' && Array.isArray(allowedSections);
  const visibleSections = customRoleActive
    ? portal.sections.filter(s => allowedSections.includes(s.id))
    : portal.sections;

  const filteredSections = visibleSections.map(s => ({ ...s, locked: isSectionLocked(s.id) }));

  return (
    <aside className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
      {/* Brand */}
      <div style={{
        padding: collapsed ? '20px 12px' : '20px',
        borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', gap: 10, minHeight: 64,
      }}>
        {tenantLogo ? (
          <img src={tenantLogo} alt="" style={{ height: 34, maxWidth: collapsed ? 34 : 120, objectFit: 'contain', borderRadius: 6 }} onError={e => { e.target.style.display = 'none'; }} />
        ) : (
          <BrandLogo size={30} showText={!collapsed} />
        )}
      </div>

      {/* Portal selector — SuperAdmin can switch */}
      <div style={{ padding: collapsed ? '12px 8px' : '12px 16px', borderBottom: '1px solid var(--border)' }}>
        <button onClick={() => isSuperAdmin && !collapsed && setShowSwitcher(!showSwitcher)} style={{
          width: '100%', padding: collapsed ? 8 : '8px 12px',
          background: `color-mix(in srgb, ${portal.color} 10%, transparent)`,
          border: `1px solid color-mix(in srgb, ${portal.color} 25%, transparent)`,
          borderRadius: 8, display: 'flex', alignItems: 'center', gap: 8,
          justifyContent: collapsed ? 'center' : 'flex-start',
          cursor: isSuperAdmin ? 'pointer' : 'default',
        }}>
          <PortalIcon size={16} style={{ color: portal.color }} />
          {!collapsed && (
            <span className="portal-label" style={{ fontSize: 12, fontWeight: 600, color: portal.color, whiteSpace: 'nowrap', flex: 1, textAlign: 'left' }}>
              {portal.label}
            </span>
          )}
          {!collapsed && isSuperAdmin && (
            <ChevronDown size={14} style={{ color: portal.color, transform: showSwitcher ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
          )}
        </button>

        {/* Portal switcher dropdown */}
        {showSwitcher && isSuperAdmin && !collapsed && (
          <div style={{ marginTop: 6, background: 'var(--card)', borderRadius: 8, border: '1px solid var(--border)', overflow: 'hidden' }}>
            {Object.entries(PORTAL_CONFIG).map(([key, cfg]) => (
              <button key={key} onClick={() => {
                // SA → su propio panel. Cualquier otro rol → elegir un usuario real
                // de ese rol y entrar como él (suplantación con permisos reales).
                if (key === 'superadmin') onViewAsChange?.(null);
                else setPickerRole(key);
                setShowSwitcher(false);
              }} style={{
                width: '100%', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8,
                background: effectiveRole === key ? `color-mix(in srgb, ${cfg.color} 12%, transparent)` : 'transparent',
                border: 'none', cursor: 'pointer', borderBottom: '1px solid var(--border)',
              }}
                onMouseEnter={e => { if (effectiveRole !== key) e.currentTarget.style.background = 'var(--card-hover)'; }}
                onMouseLeave={e => { if (effectiveRole !== key) e.currentTarget.style.background = 'transparent'; }}
              >
                {(() => { const I = cfg.icon; return <I size={14} style={{ color: cfg.color }} />; })()}
                <span style={{ fontSize: 12, color: effectiveRole === key ? cfg.color : 'var(--text-muted)', fontWeight: 600 }}>{cfg.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Navigation — agrupada y colapsable por header. El estado de cada
          grupo se persiste en localStorage por rol. La sección activa fuerza
          expandido. En modo collapsed (sidebar reducido) los headers no se
          muestran y todas las secciones están visibles como iconos. */}
      <nav style={{ flex: 1, padding: 8, overflow: 'auto' }}>
        {(() => {
          // Agrupar secciones por 'group' preservando orden.
          const groups = [];
          const indexByGroup = new Map();
          for (const s of filteredSections) {
            const g = s.group || '__ungrouped__';
            if (!indexByGroup.has(g)) {
              indexByGroup.set(g, groups.length);
              groups.push({ name: s.group, sections: [] });
            }
            groups[indexByGroup.get(g)].sections.push(s);
          }

          return groups.map((group, gi) => {
            const isCollapsedGroup = !collapsed && group.name && collapsedGroups.has(group.name);
            const groupHasActive = group.sections.some(s => s.id === currentSection);
            return (
              <div key={group.name || `g${gi}`} style={{ marginBottom: 4 }}>
                {!collapsed && group.name && (
                  <button
                    onClick={() => toggleGroup(group.name)}
                    aria-expanded={!isCollapsedGroup}
                    style={{
                      position: 'relative',
                      width: '100%', display: 'flex', alignItems: 'center', gap: 6,
                      padding: '12px 8px 6px 16px', background: 'transparent', border: 'none',
                      cursor: 'pointer', userSelect: 'none', textAlign: 'left',
                    }}
                  >
                    {/* Banda vertical visible en TODOS los grupos como divisor de sección.
                        El grupo con la sección activa la pinta con el accent del portal,
                        el resto mantiene un tono neutro suave. */}
                    <span aria-hidden="true" style={{
                      position: 'absolute',
                      left: 4, top: 14, bottom: 8,
                      width: 3, borderRadius: 2,
                      background: groupHasActive ? portal.color : 'var(--border)',
                      opacity: groupHasActive ? 1 : 0.7,
                      transition: 'background 0.15s, opacity 0.15s',
                    }} />
                    <ChevronDown size={11} style={{
                      color: 'var(--text-dim)',
                      transform: isCollapsedGroup ? 'rotate(-90deg)' : 'none',
                      transition: 'transform 0.15s',
                      flexShrink: 0,
                    }} />
                    <span style={{
                      fontSize: 10, fontWeight: 700, letterSpacing: '0.6px',
                      color: groupHasActive ? portal.color : 'var(--text-dim)',
                      textTransform: 'uppercase', flex: 1,
                    }}>{group.name}</span>
                  </button>
                )}
                {!isCollapsedGroup && group.sections.map(s => {
                  const SIcon = s.icon;
                  const isActive = currentSection === s.id && !s.locked;
                  const locked = s.locked;
                  return (
                    <button
                      key={s.id}
                      data-tour={s.id}
                      data-locked={locked ? 'true' : undefined}
                      title={locked ? `${s.label} — No incluido en tu plan` : s.label}
                      aria-disabled={locked || undefined}
                      onClick={() => {
                        if (locked) { setUpgradeFeature(s.label); return; }
                        onSectionChange(s.id);
                      }}
                      style={{
                        position: 'relative',
                        width: '100%', padding: collapsed ? 10 : '10px 12px',
                        display: 'flex', alignItems: 'center', gap: 10,
                        background: isActive ? `color-mix(in srgb, ${portal.color} 12%, transparent)` : 'transparent',
                        border: 'none', borderRadius: 8, marginBottom: 2,
                        justifyContent: collapsed ? 'center' : 'flex-start',
                        transition: 'all 0.15s ease',
                        opacity: locked ? 0.6 : 1,
                        cursor: 'pointer',
                      }}
                      onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'var(--card-hover)'; }}
                      onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                    >
                      <SIcon size={18} style={{ color: isActive ? portal.color : 'var(--text-muted)', flexShrink: 0 }} />
                      {!collapsed && (
                        <>
                          <span className="nav-label" style={{
                            fontSize: 13, fontWeight: isActive ? 600 : 500,
                            color: isActive ? 'var(--text)' : 'var(--text-muted)', whiteSpace: 'nowrap', flex: 1, textAlign: 'left',
                          }}>{s.label}</span>
                          {locked && (
                            <Lock size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} aria-label="No incluido en tu plan" />
                          )}
                        </>
                      )}
                      {collapsed && locked && (
                        <Lock size={10} style={{ color: 'var(--text-muted)', position: 'absolute', top: 6, right: 6 }} aria-hidden="true" />
                      )}
                    </button>
                  );
                })}
              </div>
            );
          });
        })()}
      </nav>

      {/* Upgrade modal (opens when user clicks a locked section) */}
      <UpgradeModal
        open={!!upgradeFeature}
        feature={upgradeFeature}
        onClose={() => setUpgradeFeature(null)}
        onGoToPlans={() => onSectionChange('settings')}
      />

      {/* User info & collapse */}
      <div style={{ borderTop: '1px solid var(--border)', padding: collapsed ? '12px 8px' : '12px 16px' }}>
        {!collapsed && user && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <button
              data-tour="user-footer"
              onClick={() => onSectionChange('profile')}
              title="Mi cuenta"
              style={{
                display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0,
                background: currentSection === 'profile' ? 'var(--card-hover)' : 'none',
                border: 'none', padding: 4, borderRadius: 6, cursor: 'pointer', textAlign: 'left',
              }}
              onMouseEnter={e => { if (currentSection !== 'profile') e.currentTarget.style.background = 'var(--card-hover)'; }}
              onMouseLeave={e => { if (currentSection !== 'profile') e.currentTarget.style.background = 'none'; }}
            >
              <div style={{
                width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                background: `linear-gradient(135deg, ${portal.color}, color-mix(in srgb, ${portal.color} 40%, var(--bg)))`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, fontWeight: 700, color: '#fff',
              }}>
                {(user.first_name?.[0] || user.email?.[0] || '?').toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {user.first_name} {user.last_name}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {user.email}
                </div>
              </div>
            </button>
            <button onClick={logout} style={{ background: 'none', border: 'none', padding: 4, color: 'var(--text-dim)', flexShrink: 0, cursor: 'pointer' }}
              title="Cerrar sesión">
              <LogOut size={16} />
            </button>
          </div>
        )}
        <button onClick={() => setCollapsed(!collapsed)} style={{
          width: '100%', padding: 8, background: 'none', border: 'none',
          display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6,
          color: 'var(--text-muted)',
        }}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--card-hover)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
          <ChevronLeft size={18} style={{ transform: collapsed ? 'rotate(180deg)' : 'none', transition: 'transform 0.25s' }} />
        </button>
      </div>
      <ImpersonationPicker open={pickerRole !== undefined} initialRole={pickerRole || null} onClose={() => setPickerRole(undefined)} />
    </aside>
  );
}

export { PORTAL_CONFIG };
